import { useMemo, useState } from 'react'
import {
  Button,
  DataTable,
  DateRangePicker,
  Drawer,
  Field,
  Pager,
  Segmented,
  Select,
  Skeleton,
  StateBlock,
  resolveRangePreset,
  type Column,
  type DateRangePresetId,
} from '@w7/ui'
import { useAllTm } from '@/api/hooks/reports/useAllTm'
import { useCommissionDownline } from '@/api/hooks/reports/useCommissionDownline'
import {
  useCommissionEligibility,
  useCommissionEventBets,
} from '@/api/hooks/reports/useCommissionEligibility'
import {
  COMMISSION_ELIGIBILITY_PAGE_SIZE,
  COMMISSION_MAX_RANGE_DAYS,
  defaultEligibilityWeek,
  type CommissionChildUser,
  type CommissionEligibilityRow,
  type CommissionEligibilityTab,
  type CommissionEventBetRow,
} from '@/api/map/commissionEligibility'
import { AsOf } from '@/app/bits'
import { CmPanel } from '@/features/catalog/CmPanel'
import { CommissionMemberPicker } from '@/features/reports/CommissionMemberPicker'
import { count } from '@/lib/format'
import './commission-eligibility.css'

/** Lab commission week presets — calendar popover with From/To date inputs. */
const WEEK_PRESETS: DateRangePresetId[] = ['this-week', 'last-week', 'custom']

/** Lab trigger format: `05.10.2026 - 11.10.2026`. */
export function labWeekSummary(from: string, to: string): string {
  const fmt = (iso: string) => {
    const [y, m, d] = iso.split('-')
    if (!y || !m || !d) return iso
    return `${d}.${m}.${y}`
  }
  if (!from || !to) return 'Select a week'
  return `${fmt(from)} - ${fmt(to)}`
}

export function weekPresetOf(from: string, to: string, today?: Date): DateRangePresetId {
  for (const id of WEEK_PRESETS) {
    if (id === 'custom') continue
    const resolved = resolveRangePreset(id, today)
    if (resolved && resolved.from === from && resolved.to === to) return id
  }
  return 'custom'
}

function nodeLabel(node: { name: string; username?: string }): string {
  return node.username || node.name
}

type Applied = {
  tmId: string
  dmId: string
  smId: string
  masterId: string
  members: CommissionChildUser[]
  from: string
  to: string
  tab: CommissionEligibilityTab
  run: number
}

/**
 * Commission eligibility — Lab `#!/commission-report`.
 *
 * Week + TM→DM→SM→M cascade + member chips (bqGetChildUser) + Eligible / Ineligible.
 * Live: CM-API `POST /v1/account/bqEligibleUsers` (+ bqIneligible / event bets / bqGetChildUser).
 */
export function CommissionEligibilityPage() {
  const week0 = useMemo(() => defaultEligibilityWeek(), [])
  const [from, setFrom] = useState(week0.from)
  const [to, setTo] = useState(week0.to)
  const [tmId, setTmId] = useState('')
  const [dmId, setDmId] = useState('')
  const [smId, setSmId] = useState('')
  const [masterId, setMasterId] = useState('')
  const [members, setMembers] = useState<CommissionChildUser[]>([])
  const [tab, setTab] = useState<CommissionEligibilityTab>('eligible')
  const [page, setPage] = useState(1)
  const [eventsFor, setEventsFor] = useState<CommissionEligibilityRow | null>(null)
  const [applied, setApplied] = useState<Applied | null>(null)

  const tmsQ = useAllTm(true)
  const dmsQ = useCommissionDownline(tmId || undefined, 3, Boolean(tmId))
  const smsQ = useCommissionDownline(dmId || undefined, 4, Boolean(dmId))
  const mastersQ = useCommissionDownline(smId || undefined, 5, Boolean(smId))

  const query = useMemo(
    () => ({
      tripleMaster: applied?.tmId || undefined,
      doubleMaster: applied?.dmId || undefined,
      superMaster: applied?.smId || undefined,
      master: applied?.masterId || undefined,
      memberIds: (applied?.members ?? []).map((m) => m.id),
      from: applied?.from ?? from,
      to: applied?.to ?? to,
      tab: applied?.tab ?? tab,
      page,
      pageSize: COMMISSION_ELIGIBILITY_PAGE_SIZE,
    }),
    [applied, from, to, tab, page],
  )

  const report = useCommissionEligibility(query, Boolean(applied))
  const eventBets = useCommissionEventBets(
    {
      from: applied?.from ?? '',
      to: applied?.to ?? '',
      playerId: eventsFor?.id ?? '',
      isEligible: (applied?.tab ?? tab) === 'eligible',
    },
    Boolean(eventsFor && applied),
  )

  const tmOptions = useMemo(
    () => [
      { value: '', label: 'All Triple Masters' },
      ...(tmsQ.data ?? []).map((tm) => ({ value: tm._id, label: tm.username })),
    ],
    [tmsQ.data],
  )
  const dmOptions = useMemo(
    () => [
      { value: '', label: 'Select or search DM…' },
      ...(dmsQ.data ?? []).map((n) => ({ value: n.id, label: nodeLabel(n) })),
    ],
    [dmsQ.data],
  )
  const smOptions = useMemo(
    () => [
      { value: '', label: 'Select or search SM…' },
      ...(smsQ.data ?? []).map((n) => ({ value: n.id, label: nodeLabel(n) })),
    ],
    [smsQ.data],
  )
  const masterOptions = useMemo(
    () => [
      { value: '', label: 'Select or search M…' },
      ...(mastersQ.data ?? []).map((n) => ({ value: n.id, label: nodeLabel(n) })),
    ],
    [mastersQ.data],
  )

  const bumpSearch = (nextTab: CommissionEligibilityTab = tab) => {
    setPage(1)
    setApplied({
      tmId,
      dmId,
      smId,
      masterId,
      members,
      from,
      to,
      tab: nextTab,
      run: (applied?.run ?? 0) + 1,
    })
  }

  const clearFilters = () => {
    const week = defaultEligibilityWeek()
    setFrom(week.from)
    setTo(week.to)
    setTmId('')
    setDmId('')
    setSmId('')
    setMasterId('')
    setMembers([])
    setTab('eligible')
    setPage(1)
    setApplied(null)
    setEventsFor(null)
  }

  const columns: Column<CommissionEligibilityRow>[] = [
    {
      id: 'player',
      header: 'Player Name',
      cell: (row) => row.playerName || '—',
    },
    {
      id: 'master',
      header: 'Master Name',
      cell: (row) => row.masterName || '—',
      hideBelow: 720,
    },
    {
      id: 'sm',
      header: 'Super Master',
      cell: (row) => row.superMasterName || '—',
      hideBelow: 900,
    },
    {
      id: 'dm',
      header: 'Double Master',
      cell: (row) => row.doubleMasterName || '—',
      hideBelow: 1100,
    },
    {
      id: 'tm',
      header: 'Triple Master',
      cell: (row) => row.tripleMasterName || '—',
      hideBelow: 1200,
    },
    {
      id: 'events',
      header: 'Events Played',
      numeric: true,
      cell: (row) => (
        <button
          type="button"
          className="cm-commission-link"
          onClick={() => setEventsFor(row)}
        >
          {count(row.totalEventsPlayed)}
        </button>
      ),
    },
    {
      id: 'bets',
      header: 'Bets Played',
      numeric: true,
      cell: (row) => count(row.totalBetsPlayed),
    },
  ]

  const rows = report.data?.rows ?? []
  const total = report.data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / COMMISSION_ELIGIBILITY_PAGE_SIZE) || 1)
  const activeTab = applied?.tab ?? tab
  const drawerEvents: CommissionEventBetRow[] =
    eventBets.data && eventBets.data.length > 0
      ? eventBets.data
      : (eventsFor?.events ?? [])

  const eventColumns: Column<CommissionEventBetRow>[] = [
    {
      id: 'date',
      header: 'Date',
      cell: (row) => row.date || '—',
    },
    {
      id: 'event',
      header: 'Event Name',
      cell: (row) => row.eventName || '—',
    },
    {
      id: 'bets',
      header: 'Bets Played',
      numeric: true,
      cell: (row) => count(row.bets),
    },
  ]

  return (
    <CmPanel
      title="Commission Report"
      subtitle="Eligible / Ineligible players for the selected week — Lab commission-report"
      wide
      actions={report.data && applied ? <AsOf at={report.data.asOf} /> : null}
    >
      <div className="cm-commission-elig">
        <div className="cm-commission-elig-filters" role="region" aria-label="Commission filters">
          <div className="cm-commission-elig-hierarchy">
            <Field label="Select TM">
              {({ id, describedBy }) => (
                <Select
                  id={id}
                  aria-describedby={describedBy}
                  value={tmId}
                  onChange={(e) => {
                    setTmId(e.target.value)
                    setDmId('')
                    setSmId('')
                    setMasterId('')
                    setMembers([])
                  }}
                  options={tmOptions}
                />
              )}
            </Field>
            <Field label="Select DM">
              {({ id, describedBy }) => (
                <Select
                  id={id}
                  aria-describedby={describedBy}
                  value={dmId}
                  disabled={!tmId}
                  onChange={(e) => {
                    setDmId(e.target.value)
                    setSmId('')
                    setMasterId('')
                    setMembers([])
                  }}
                  options={dmOptions}
                />
              )}
            </Field>
            <Field label="Select SM">
              {({ id, describedBy }) => (
                <Select
                  id={id}
                  aria-describedby={describedBy}
                  value={smId}
                  disabled={!dmId}
                  onChange={(e) => {
                    setSmId(e.target.value)
                    setMasterId('')
                    setMembers([])
                  }}
                  options={smOptions}
                />
              )}
            </Field>
            <Field label="Select M">
              {({ id, describedBy }) => (
                <Select
                  id={id}
                  aria-describedby={describedBy}
                  value={masterId}
                  disabled={!smId}
                  onChange={(e) => {
                    setMasterId(e.target.value)
                    setMembers([])
                  }}
                  options={masterOptions}
                />
              )}
            </Field>
          </div>

          <CommissionMemberPicker
            selected={members}
            onChange={setMembers}
            tripleMaster={tmId || undefined}
            doubleMaster={dmId || undefined}
            superMaster={smId || undefined}
            master={masterId || undefined}
          />

          <div className="cm-commission-elig-week-row">
            <Field label="Select Week">
              {() => (
                <DateRangePicker
                  label="Select Week"
                  preset={weekPresetOf(from, to)}
                  value={{ from, to }}
                  summary={labWeekSummary(from, to)}
                  presets={WEEK_PRESETS}
                  maxSpanDays={COMMISSION_MAX_RANGE_DAYS}
                  onChange={(next) => {
                    setFrom(next.from)
                    setTo(next.to)
                  }}
                  className="cm-commission-week-trigger"
                />
              )}
            </Field>
            <div className="cm-commission-elig-actions">
              <Button
                variant="primary"
                disabled={!from || !to || report.isFetching}
                busy={report.isFetching}
                onClick={() => bumpSearch(tab)}
              >
                Search
              </Button>
              <Button variant="ghost" onClick={clearFilters}>
                Clear
              </Button>
            </div>
          </div>
        </div>

        {!applied ? (
          <StateBlock icon="commission" title="Choose a week">
            Defaults to last week. Narrow TM → DM → SM → M if needed, then Search — same flow as
            Lab Commission Report.
          </StateBlock>
        ) : report.isLoading ? (
          <Skeleton height={220} />
        ) : report.data?.unavailable ? (
          <StateBlock icon="info" title="Commission eligibility unavailable">
            {report.data.reason ?? 'Could not load commission eligibility from CM-API.'}
          </StateBlock>
        ) : (
          <>
            <Segmented
              label="Status"
              value={activeTab}
              onChange={(next) => {
                setTab(next)
                setPage(1)
                setApplied((prev) =>
                  prev
                    ? { ...prev, tab: next, run: prev.run + 1 }
                    : prev,
                )
              }}
              items={[
                { id: 'eligible', label: 'Eligible' },
                { id: 'ineligible', label: 'Ineligible' },
              ]}
            />
            <DataTable
              label={`${activeTab === 'eligible' ? 'Eligible' : 'Ineligible'} players`}
              columns={columns}
              rows={rows}
              rowKey={(row) => row.id}
              empty={
                <StateBlock icon="commission" title={`No ${activeTab} players`}>
                  Nothing matched this week and scope.
                </StateBlock>
              }
            />
            {total > 0 ? (
              <Pager
                page={page}
                pageCount={pageCount}
                total={total}
                pageSize={COMMISSION_ELIGIBILITY_PAGE_SIZE}
                onChange={setPage}
              />
            ) : null}
          </>
        )}
      </div>

      <Drawer
        open={Boolean(eventsFor)}
        onClose={() => setEventsFor(null)}
        title={eventsFor ? `${eventsFor.playerName} — events` : 'Events'}
        subtitle={
          eventsFor
            ? eventBets.isLoading
              ? 'Loading…'
              : `${count(drawerEvents.reduce((sum, ev) => sum + ev.bets, 0))} bets · ${count(drawerEvents.length)} events`
            : undefined
        }
        width="wide"
      >
        {eventsFor ? (
          eventBets.isLoading ? (
            <Skeleton height={120} />
          ) : (
            <DataTable
              label={`${eventsFor.playerName} events`}
              columns={eventColumns}
              rows={drawerEvents}
              rowKey={(row, index) => row.id || `event-${index}`}
              density="compact"
              stickyOffset={0}
              empty={
                <StateBlock icon="commission" title="No events">
                  No event/day bets for this player in the selected week.
                </StateBlock>
              }
            />
          )
        ) : null}
      </Drawer>
    </CmPanel>
  )
}
